import { z } from 'zod';

/**
 * Los tarjetones que la caseta entrega a las visitas sin invitación.
 *
 * **El código lo genera Kodica y de ahí sale el QR que se manda a imprimir**,
 * así que el que va en el plástico y el que está en la base son el mismo por
 * construcción — no hay forma de teclear mal uno que después ningún escaneo
 * encontraría.
 *
 * Aun así el tarjetón **no autoriza nada**: solo cierra una visita que ya está
 * adentro, y solo en el acceso por el que entró. Por eso su código no va
 * firmado; lo peor que consigue quien copie uno es cerrar su propia visita.
 */
export const ESTADOS_TARJETON = ['DISPONIBLE', 'ENTREGADO', 'PERDIDO', 'BAJA'] as const;
export type EstadoTarjeton = (typeof ESTADOS_TARJETON)[number];

const codigo = z.string().trim().min(1, 'El tarjetón necesita su código').max(120);

export const registrarTarjetonSchema = z.object({
  /**
   * Opcional: **lo normal es que lo genere el servidor**, porque es Kodica
   * quien imprime el QR. Se admite darlo para dar de alta un lote de plásticos
   * que ya existiera impreso de antes.
   */
  codigo: codigo.optional(),
  /**
   * Lo que se lee a simple vista en el plástico. Es lo que el guardia le dice
   * al visitante —«devuélvame el 12»— y sin ello habría que deletrear un código.
   */
  etiqueta: z.string().trim().max(40).nullish(),
});
export type RegistrarTarjeton = z.infer<typeof registrarTarjetonSchema>;

/** Entregarle un tarjetón a una visita, o cerrarla al devolverlo. */
export const escanearTarjetonSchema = z.object({ codigo });
export type EscanearTarjeton = z.infer<typeof escanearTarjetonSchema>;

/**
 * Cambiar en qué anda un tarjetón.
 *
 * `PERDIDO` exige motivo y los demás no: dar de baja un plástico roto se explica
 * solo, pero uno que no volvió es una pérdida que alguien va a tener que cobrar
 * o reponer, y meses después nadie recuerda cuál fue cuál.
 */
export const cambiarEstadoTarjetonSchema = z
  .object({
    estado: z.enum(ESTADOS_TARJETON),
    motivo: z.string().trim().max(200).nullish(),
  })
  .refine((d) => d.estado !== 'PERDIDO' || (d.motivo && d.motivo.length > 0), {
    message: 'Di qué pasó con el tarjetón: quién se lo llevó, o dónde se perdió',
    path: ['motivo'],
  });
export type CambiarEstadoTarjeton = z.infer<typeof cambiarEstadoTarjetonSchema>;
